import {
  getDayRange,
  getGameTime,
  getGameTimeLocal,
  getShortDate,
  timeStampToDate,
} from '../formatting/formatting'
import {
  PLL_LINKS,
  BROADCAST_IMG,
  REVERSE_BROADCAST_IMG,
  CNBC_IMG,
  MAIN_PLL_URL,
} from '../../Config/config'
import {
  incompleteMarkets,
  isChampSeriesLive,
  STATS_YEAR,
} from '../../Config/League/league.config'
import { marketPresales } from '../../Config/Season/season'
import type { EventGQL } from '../../Api'
import { createTicketUTM } from '../../Routing'
import {
  generateHomecomingTitle,
  isHomecomingWeekend,
  SEASON_SEGMENT_NAMES,
} from '../season/seasonHelpers'
import { SeasonType } from 'Context/SeasonContext/SeasonContext'
type EventWeekParams = {
  week: string
  events: EventGQL[]
}
export type WeekTicketData = {
  week: string
  tickets: string
  dayTickets: { date: string; link: string }[]
  suitesId: string
  waitlistUrl: string
  waitlist: boolean
}

export type ClockTime = {
  period: string
  clockTime: string
}

export const eventWeeks: string[] = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
  '11',
  '12',
  '13',
  '14',
]
export const allAddEventsId = 'ZO599017'

const GamePeriodMap: { [key: string]: string } = {
  '1': '1st',
  '2': '2nd',
  '3': '3rd',
  '4': '4th',
}

type DayTicketsType = {
  date: string
  ticketUrl: string
  ticketId: string
}

/**
 *
 * Check if the season has ended
 */
export const checkSeasonStatus = (events: EventGQL[]) => {
  const firstEventStatus = events[0].eventStatus
  const finalEventStatus = events[events.length - 1].eventStatus
  if (firstEventStatus === 0) {
    // Season has not started yet
    return 0
  }
  if (finalEventStatus > 1) {
    // Season has ended
    return 2
  }
  // Season is in progress
  return 1
}

/**
 *
 * Check if the season is in the postseason or not
 */
export const isPostseason = (events: EventGQL[]) => {
  if (events.length < 1) return false

  // Check if postseason games are set yet
  const postseasonGames = events.filter(
    (ev) => ev.seasonSegment === 'post' && (ev.awayTeam !== null || ev.homeTeam !== null)
  )

  if (postseasonGames.length === 0) return false

  // Check if ANY postseason game is upcoming or live
  const activePostseasonGame = postseasonGames.find((ev) => ev.eventStatus <= 1)
  if (activePostseasonGame) {
    return true
  }

  // If all postseason games done, check if within 2 weeks
  const sortedPostseasonGames = postseasonGames.sort((a, b) => b.startTime - a.startTime)
  const finalPostseasonGame = sortedPostseasonGames[0]

  if (finalPostseasonGame && finalPostseasonGame.eventStatus > 1) {
    const twoWeeksInSeconds = 1209600
    const now = new Date().getTime() / 1000
    const timeSinceEnd = now - finalPostseasonGame.startTime

    if (timeSinceEnd < twoWeeksInSeconds) {
      return true
    }
  }

  return false
}

/**
 *
 * Return if regseason has started or not
 */
export const hasRegularSeasonStarted = (events: EventGQL[]) => {
  if (events.length < 1) return false
  const regGames = events.filter((ev) => ev.seasonSegment === 'regular')
  if (regGames.length < 1) return false

  const sorted = regGames.sort((a, b) => a.startTime - b.startTime)
  const now = new Date()
  //Set current time to seconds
  const currentTime = now.getTime() / 1000
  const diff = sorted[0].startTime - currentTime
  if (diff < 0 || sorted[0].eventStatus > 0) {
    // First reg season game has started
    return true
  } else {
    return false
  }
}

/**
 *
 * Return if postseason has started or not
 */
export const hasPostseasonStarted = (events: EventGQL[]) => {
  if (events.length < 1) return false
  const postseasonGames = events.filter((ev) => ev.seasonSegment === 'post')
  if (postseasonGames.length < 1) return false

  const sorted = postseasonGames.sort((a, b) => a.startTime - b.startTime)
  const now = new Date()
  //Set current time to seconds
  const currentTime = now.getTime() / 1000
  const diff = sorted[0].startTime - currentTime
  if (diff < 0 || sorted[0].eventStatus > 0) {
    // First postseason game has started
    return true
  } else {
    return false
  }
}

/**
 *
 * Return if champ series has started or not
 */
export const checkHasChampSeriesStarted = (events: EventGQL[]) => {
  if (events.length < 1) return false
  const champSeriesGames = events.filter((ev) => ev.seasonSegment === 'champseries')
  if (champSeriesGames.length < 1) return false
  let firstGame = champSeriesGames[0]
  if (firstGame.eventStatus > 0 && isChampSeriesLive) {
    //Champ Series is going on right now
    return true
  } else {
    return false
  }
}

/**
 *
 * Map events into specific weeks
 */
export const getWeekList = (events: EventGQL[]) => {
  let weekList: string[] = []
  events.map((e) => {
    let we = weekList.find((w) => w === e.week)
    if (!we) {
      //Week wasn't added yet
      weekList.push(e.week)
    }
    return
  })
  return weekList
}

/**
 *
 * Map events into specific weeks
 */
export const mapEventWeeks = ({ week, events }: EventWeekParams) => {
  return events.filter((event) => event.week === week)
}

/**
 *
 * Map champ series events
 */
export const mapChampSeriesEvents = (
  events: EventGQL[],
  byType?: string
): { [key: string]: EventGQL[] } => {
  if (byType === 'day') {
    return mapEventsByDay(events)
  }
  // By segment
  return {
    roundRobin: events.filter((event) =>
      event.description.toLowerCase().includes('round')
    ),
    semis: events.filter((event) => event.description.toLowerCase().includes('semi')),
    championship: events.filter((event) =>
      event.description.toLowerCase().includes('champ')
    ),
  }
}

/**
 *
 * Map events by date the game is using startTime
 */
export const mapEventsByDay = (events: EventGQL[]) => {
  const eventDays: { [key: string]: EventGQL[] } = events.reduce((acc, event) => {
    const { date } = timeStampToDate(event.startTime)
    const existingEventsInDay = acc[date] || []
    const isDuplicate = existingEventsInDay.some(
      (ev) => ev.startTime === event.startTime
    )

    return {
      ...acc,
      [date]: isDuplicate ? existingEventsInDay : [...existingEventsInDay, event],
    }
  }, {} as { [key: string]: EventGQL[] })

  return eventDays
}

/**
 *
 * Map events by market like philadelphia, boston, etc
 */
export const mapEventsByMarket = (events: EventGQL[]) => {
  const eventsByMarketAndSegment: { [key: string]: EventGQL[] } = events.reduce(
    (acc, event) => {
      const specialWeek = checkSpecialWeek(event)
      const marketKey = `${specialWeek ? `${specialWeek} - ` : ''}${event.location}`
      const existingEvents = acc[marketKey] || []

      // Check if an event with the exact same start time already exists for this market and segment
      const isDuplicate = existingEvents.some((ev) => ev.startTime === event.startTime)

      return {
        ...acc,
        [marketKey]: isDuplicate ? existingEvents : [...existingEvents, event],
      }
    },
    {} as { [key: string]: EventGQL[] }
  )

  return eventsByMarketAndSegment
}

/**
 *
 * Get current Week
 */
export const getCurrentWeek = (events: EventGQL[]) => {
  const weekRes = events.find((ev) => ev.eventStatus < 2)
  if (!weekRes) return null
  return weekRes.week
}

/**
 *
 * Get all events in current Week
 */
export const getCurrentWeekEvents = (events: EventGQL[]) => {
  const weekRes = events.find((ev) => ev.eventStatus < 2)
  if (!weekRes) return
  const wkEvents = mapEventWeeks({ week: weekRes.week, events: events })
  return wkEvents
}

/**
 *
 * Get all events in current Week
 */
export const hasWeekendGamesStarted = (events: EventGQL[]) => {
  const weekRes = events.find((ev) => ev.eventStatus < 2)
  if (!weekRes) return
  let now = Date.now() / 1000
  const wkEvents = mapEventWeeks({ week: weekRes.week, events: events })
  const started = wkEvents.find((w) => w.startTime < now || w.eventStatus > 0)
  return started ? true : false
}

/**
 *
 * Return date range of all games in a specific weekend
 */
export const getWeekendRange = (events: EventGQL[], configIncompleteList: string[]) => {
  if (events.length === 0) return
  // Only one event, grab the date
  if (events.length === 1) {
    if (checkIsMarketIncomplete(events[0].week, configIncompleteList)) {
      return getDayRange(events[0].startTime, false)
    }
    return getShortDate(events[0].startTime, false)
  }
  const sorted = events.sort((a, b) => a.startTime - b.startTime)
  const [first] = sorted.slice(0)
  const [last] = sorted.slice(-1)
  const firstDay = getShortDate(first.startTime, false)
  let lastDay = getShortDate(last.startTime, false)
  return firstDay === lastDay ? firstDay : `${firstDay}${` - ${lastDay}`}`
}

/**
 *
 * Return upcoming games, optional number to return (default 1)
 */
export const getNextMatchupsName = (evs: EventGQL[]) => {
  const upcomingEvents = evs.filter((ev) => ev.eventStatus > 0)
  if (upcomingEvents.length > 0) {
    return upcomingEvents
  }
  return []
}

/**
 *
 * Return upcoming game or final game
 */
export const getNextMatchup = (evs: EventGQL[]) => {
  const upcomingEvents = evs.filter((ev) => ev.eventStatus < 2)
  if (upcomingEvents.length > 0) {
    return upcomingEvents[0]
  }
  if (evs.length > 0 && upcomingEvents.length === 0) {
    //All games have ended, return latest game
    return evs[evs.length - 1]
  }
  return null
}

/**
 *
 * Structure tickets to use in schedule and tickets
 */
export const structureTicketObj = (week: string, events: EventGQL[], page: string) => {
  const dayTs = getDayTickets(events, page)
  const ticketObj = {
    weekend: week,
    suiteId: events[0].suiteId,
    waitList: events[0].waitlist && events[0].waitlistUrl ? true : false,
    dayTickets: dayTs || [],
  }
  return ticketObj
}

/**
 *
 * Add utm to day ticks
 */
export const createDayTicketUrl = (event: EventGQL, page: string, location?: string) => {
  if (!page) return ''
  if (event.seasonSegment === 'champseries') {
    return PLL_LINKS.championshipSeriesTickets
  }
  if (event.waitlistUrl) return event.waitlistUrl
  if (!event.urlTicket) return PLL_LINKS.schedule

  return createTicketUTM({
    ticketId: event.urlTicket,
    location: location ?? 'Schedule',
  })
}

/**
 *
 * Set up weekend ticket url
 * @param events - events to set up
 * @returns weekend ticket url
 */
export const setupWeekendUrl = (events: EventGQL[], location?: string) => {
  if (events[0]?.weekendTicketId) {
    return createTicketUTM({
      ticketId: events[0].weekendTicketId,
      location: location ?? 'Schedule',
    })
  }
  return
}

/**
 *
 * Turns events into ticket objects
 */
export const getDayTickets = (events: EventGQL[], page: string) => {
  let tks: DayTicketsType[] = []
  let prevDate: string = ''
  let day = 1
  if (events.length === 1) {
    //Only 1 day ticket
    if (!events[0].urlTicket) return
    const d = getShortDate(events[0].startTime)
    const dayTix = createDayTicketUrl(events[0], page)
    tks = [
      {
        date: d,
        ticketUrl: dayTix || events[0].waitlistUrl,
        ticketId: events[0]?.ticketId || '',
      },
    ]
  } else {
    events.map((e) => {
      let d = getShortDate(e.startTime)
      if (d === prevDate) return
      day++ //Increment day for next time
      if (!e.urlTicket) return
      const dayTix = createDayTicketUrl(e, page)
      tks.push({
        date: d,
        ticketUrl: dayTix || e.waitlistUrl,
        ticketId: e?.ticketId || '',
      })
      prevDate = d
    })
  }
  return tks
}

/**
 *
 * Enhanced ticker start with focus on most relevant current game
 */
export const determineTickerStart = (events: EventGQL[]): number => {
  if (!events.length) return 0
  const now = Date.now() / 1000
  const liveGame = checkLiveGameRunning(events)
  if (liveGame) {
    return events.findIndex((ev) => ev.id === liveGame.id)
  }

  // Find next upcoming game
  const upcomingGames = events.filter((ev) => ev.eventStatus === 0)

  if (upcomingGames.length > 0) {
    const nextGame = upcomingGames[0]
    const nextGameIndex = events.findIndex((ev) => ev.id === nextGame.id)

    // If next game is within the next 5 days, show it
    const daysUntilGame = (nextGame.startTime - now) / (24 * 60 * 60)
    if (daysUntilGame <= 5) {
      return nextGameIndex !== -1 ? nextGameIndex : 0
    }
  }

  // Find recently completed games (within last 3 days)
  const recentlyCompleted = events.filter((ev) => {
    if (ev.eventStatus < 2) return false
    const daysSinceGame = (now - ev.startTime) / (24 * 60 * 60)
    return daysSinceGame <= 3
  })

  if (recentlyCompleted.length > 0) {
    const mostRecent = recentlyCompleted[0]
    const recentIndex = events.findIndex((ev) => ev.id === mostRecent.id)
    return recentIndex !== -1 ? recentIndex : 0
  }

  return determineBaseTickerStart(events)
}

/**
 *
 * Shifting events that already happened to the back
 */
export const determineBaseTickerStart = (events: EventGQL[]) => {
  const start = events.find((ev) => ev.eventStatus < 2)
  if (!start) {
    //last Item
    return events.length - 1
  }
  return events.indexOf(start)
}

/**
 *
 * Return previewUrl for specific event
 */
export const getPreviewUrl = (slug: string, seasonSegment?: SeasonType) => {
  const isAllStarEvent =
    seasonSegment === SEASON_SEGMENT_NAMES.allstar || slug.includes('all_star')
  if (isAllStarEvent) {
    return PLL_LINKS.allstar
  } else {
    return `https://${MAIN_PLL_URL}/matchup/${slug}`
  }
}

/**
 *
 * Update the location of venues that are more specific
 */

const postSeasonLocationMap: Record<string, string> = {
  'Boston, MA': 'Foxborough, MA',
  'Philadelphia, PA': 'Chester, PA',
}

const regularSeasonLocationMap: Record<string, string> = {
  'Dallas, TX': 'Frisco, TX',
  'Seattle, WA': 'Tacoma, WA',
  'Philadelphia, PA': 'Villanova, PA',
  'Salt Lake City, UT': 'Herriman, UT',
  'Boston, MA': 'Cambridge, MA',
  'Minneapolis, MN': 'Eagan, MN',
  'Long Island, NY': 'Hempstead, NY',
}

export const createVenueLocation = (location: string, seasonSegment: string): string => {
  if (seasonSegment === 'post') {
    return postSeasonLocationMap[location] || location
  }
  return regularSeasonLocationMap[location] || location
}

/**
 *
 * Checking if this is a special week
 */
export const isWeekSpecial = (event: EventGQL) => {
  if (!event || !event.seasonSegment) return
  if (event.week === '1') return true
  switch (event.seasonSegment) {
    case 'regular':
      return false
    case 'allstar':
      return true
    case 'post':
      return true
    default:
      return false
  }
}

/**
 *
 * Get weekends that are special like  Playoffs
 */
export const checkSpecialWeek = (event: EventGQL, useHomecoming?: boolean) => {
  if (!event || !event.seasonSegment) return
  let market = getMarketSlug(event.location)
  if (market) {
    let homecomingTeam = isHomecomingWeekend(market, event.seasonSegment)
    if (useHomecoming && homecomingTeam) return generateHomecomingTitle(homecomingTeam)
  }

  switch (event.seasonSegment) {
    case 'regular':
      if (!event?.description) return null
      if (
        event.description.toLowerCase().includes('throwback') ||
        event.description.toLowerCase().includes('indigenous')
      ) {
        return event.description
      }
      return null
    case 'allstar':
      return 'Lexus All-Star'
    case 'post':
      if (!event?.description) return null
      if (event.description.toLowerCase().includes('quarterfinal')) {
        return 'Quarterfinals'
      }
      if (event.description.toLowerCase().includes('semifinal')) {
        return 'Semifinals'
      }
      if (event.description.toLowerCase().includes('championship')) {
        return 'Championship'
      }
      return event.description
    case 'champseries':
      return 'Lexus Championship Series'
    default:
      return null
  }
}

/**
 *
 * Get short week name
 */
export const getShortWeekName = (event: EventGQL) => {
  if (!event || !event.seasonSegment) return

  switch (event.seasonSegment) {
    case 'regular':
      return event.week
    case 'allstar':
      return 'AS'
    case 'post':
      if (!event?.description) return null
      if (event.description.toLowerCase().includes('quarterfinal')) {
        return 'QF'
      }
      if (event.description.toLowerCase().includes('semifinal')) {
        return 'SF'
      }
      if (event.description.toLowerCase().includes('championship')) {
        return 'CH'
      }
      return event.description
    case 'champseries':
      return 'CS'
    default:
      return null
  }
}

/**
 *
 * Return all broadcaster images for specific event
 */
export const getBroadcastImg = (broadcasters: string[]) => {
  const broadcastImgs = broadcasters.map((name) => {
    switch (name) {
      case 'ESPN':
        return `${BROADCAST_IMG}/${name}_Main.png`
      case 'ESPN2':
        return `${BROADCAST_IMG}/${name}_Main.png`
      case 'ABC':
        return `${BROADCAST_IMG}/${name}_Main.png`
      case 'ESPNU':
        return `${BROADCAST_IMG}/${name}_Black.png`
      case 'ESPN+':
        return `${BROADCAST_IMG}/${name.replace('+', '%2B')}_Black.png`
      case 'Peacock':
        return `${BROADCAST_IMG}/${name}_Black.png`
      case 'NBC':
        return `${BROADCAST_IMG}/${name}_Black.png`
      case 'CNBC':
        return `${CNBC_IMG}/${name}.png`
      default:
        return ''
    }
  })
  return broadcastImgs
}

/**
 *
 * Return all broadcaster images for specific event
 */
export const getReverseBroadcastImg = (broadcasters: string[]) => {
  const broadcastImgs = broadcasters.map((name) =>
    name === ''
      ? ''
      : name === 'CNBC'
      ? `${CNBC_IMG}/${name}.png`
      : name === 'ABC'
      ? `${REVERSE_BROADCAST_IMG}/${name.replace('+', '%2B')}_Main.png`
      : `${REVERSE_BROADCAST_IMG}/${name.replace('+', '%2B')}_White.png`
  )
  return broadcastImgs
}

/**
 *
 * Return the period and clock time of a specific game
 */
export const getPeriodAndClockTime = (event: EventGQL) => {
  if (!event) return
  let period: string | number = ''
  let clockTime = ''
  if (event.period > 5) {
    const otNumber = event.period - 4 //If second overtime, event.period would be 6
    period = `OT${otNumber}`
  } else {
    period = event.period === 5 ? 'OT' : GamePeriodMap[event.period]
  }

  if (event?.period === 1 && event?.clockMinutes === 0 && event?.clockSeconds === 0) {
    return { period: 'End of 1st', clockTime }
  }

  if (event?.period === 2 && event?.clockMinutes === 0 && event?.clockSeconds === 0) {
    return { period: 'Half', clockTime }
  }

  if (event?.period === 3 && event?.clockMinutes === 0 && event?.clockSeconds === 0) {
    return { period: 'End of 3rd', clockTime }
  }

  if (
    event?.period === 4 &&
    event?.clockMinutes === 0 &&
    event?.clockSeconds === 0 &&
    event?.visitorScore === event.homeScore
  ) {
    return { period: 'End of Reg', clockTime }
  }
  if (event?.clockMinutes === null || event?.clockMinutes === undefined) {
    return { period, clockTime }
  }

  const clockSecs = formatClockSeconds(event?.clockSeconds)
  if (!clockSecs) return { period, clockTime }
  //Set actual clock time
  clockTime = `${event?.clockMinutes || 0}:${clockSecs}`
  return {
    period,
    clockTime,
  }
}

/**
 *
 * Format clock seconds for scoreboard
 */
export const formatClockSeconds = (seconds?: number | null) => {
  if (seconds === undefined || seconds === null) return ''
  return seconds === 0 ? '00' : seconds < 10 ? `0${seconds}` : `${seconds}`
}

/**
 *
 * Return text to display in button based on event status
 */
export const getActionText = (status: number) => {
  return status === 0 ? 'Preview' : status === 1 ? 'Watch Live' : 'Game Stats'
}

/**
 *
 * Return link  based on event status
 */
export const getEventActionLink = (event: EventGQL) => {
  switch (event.eventStatus) {
    case 0:
      return getPreviewUrl(event.slugname)
    case 1:
      return event.urlStreaming || PLL_LINKS.watch
    case 2:
      return `${PLL_LINKS.stats}/games/${event.year || STATS_YEAR}/${event.slugname}`
    case 3:
      return `${PLL_LINKS.stats}/games/${event.year || STATS_YEAR}/${event.slugname}`
    default:
      return getPreviewUrl(event.slugname)
  }
}

/**
 *
 * Return name of the next bracket game
 */
export const getNextMatchupName = (eventId: number) => {
  switch (eventId) {
    case 99:
      return 'reach the Championship'
    case 100:
      return 'reach the Championship'
    case 101:
      return 'win the Championship'
    default:
      return null
  }
}

/**
 *
 * Return if market has all data (used for before announcing dates and times)
 */
export const checkIsMarketIncomplete = (
  week: string,
  configIncompleteList: string[]
) => {
  if (configIncompleteList.length !== 0) {
    return configIncompleteList.includes(week)
  }
  if (incompleteMarkets.length === 0) return false
  return incompleteMarkets.includes(week)
}

/**
 *
 * Return market if it exists from list. Checks for postseason as well
 */
export const getShortMarket = (event: EventGQL) => {
  if (!event) return ''
  if (event.seasonSegment === 'post') {
    return event.slugname.includes('quarterfinal')
      ? 'quarterfinals'
      : event.slugname.includes('semifinal')
      ? 'semifinals'
      : 'championship'
  }
  if (!event.location) return ''
  if (event.location === 'Washington, D.C.') return 'washington-dc'
  return event.location.split(',')[0].replaceAll(' ', '-').toLowerCase()
}

/**
 *
 * Get market presale link
 */
export const getMarketPresale = (market: string) => {
  const mkt = getMarketSlug(market)
  if (!mkt) return ''
  const foundPresale = Object.keys(marketPresales).find((m) => m === mkt)
  return foundPresale ? marketPresales[mkt] : ''
}

/**
 *
 * Get market slug
 */
export const getMarketSlug = (market: string) => {
  if (!market) return ''
  return market.split(', ')[0].replaceAll(' ', '-').toLowerCase()
}

/**
 *
 * TEMP Check if Championship is incomplete, used for when champ date is unknown
 */
export const isIncompleteChampionship = (
  slugname: string,
  week: string,
  configIncompleteList: string[]
) => {
  if (
    slugname.includes('championship') &&
    checkIsMarketIncomplete(week, configIncompleteList)
  ) {
    return true
  }
  return false
}

/**
 *
 * Returns the date and time of the event
 */
export const renderDateAndTime = (
  startTime: number,
  showInET: boolean = false,
  hideDate: boolean = false
) => {
  if (hideDate) return ''
  let shortDate = getShortDate(startTime)
  //If showing in eastern time is requested
  let time = showInET ? getGameTime(startTime) : getGameTimeLocal(startTime)
  let etText = showInET ? 'ET' : ''
  return `${shortDate} • ${time} ${etText}`
}

/**
 *
 * Returns the date and time of the event
 */
export const renderGameTime = (startTime: number, showInET: boolean = false) => {
  //If showing in eastern time is requested
  const time = showInET ? getGameTime(startTime) : getGameTimeLocal(startTime)
  const etText = showInET ? 'ET' : ''
  return `${time} ${etText}`
}

/**
 *
 * Returns event if there is a game that is past it's start time, but still is set to 0
 * Game is either delayed or events are stale
 * Used to check if events are stale and need to be refetched
 */
export const checkPastStartGameRunning = (evs: EventGQL[]) => {
  return evs.find((ev) => isPastStartGameRunning(ev))
}

/**
 *
 * Returns event if there is a live game
 */
export const checkLiveGameRunning = (evs: EventGQL[]) => {
  return evs.find((ev) => isLiveGame(ev))
}

/**
 *
 * Check if game is live
 */
export const isLiveGame = (ev: EventGQL) => {
  if (!ev?.startTime) return false
  const now = new Date().getTime() / 1000
  const diff = now - ev.startTime

  if (diff > 0 && ev.eventStatus === 1) return true
  return false
}

/**
 *
 * Check if game eventStatus is passed startTime, but still 0
 * Game is either delayed or events are stale
 */
export const isPastStartGameRunning = (ev: EventGQL) => {
  //Ignore games that have started or ended
  if (!ev?.startTime || ev.eventStatus !== 0) return false

  const now = new Date().getTime() / 1000
  const diff = now - ev.startTime

  if (diff > 0) return true
  return false
}
